import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import Icon from 'components/AppIcon';

const ToastContext = createContext(null);

const TOAST_ICONS = {
  success: { name: 'CheckCircle', color: '#16A34A' },
  error: { name: 'XCircle', color: '#DC2626' },
  warning: { name: 'AlertTriangle', color: '#F97316' },
  info: { name: 'Info', color: '#3B82F6' },
};

const TOAST_STYLES = {
  success: { borderColor: 'rgba(22,163,74,0.4)', backgroundColor: 'rgba(22,163,74,0.08)' },
  error: { borderColor: 'rgba(220,38,38,0.4)', backgroundColor: 'rgba(220,38,38,0.08)' },
  warning: { borderColor: 'rgba(249,115,22,0.4)', backgroundColor: 'rgba(249,115,22,0.08)' },
  info: { borderColor: 'rgba(59,130,246,0.4)', backgroundColor: 'rgba(59,130,246,0.08)' },
};

const AUTO_DISMISS = { success: 4000, info: 4000, warning: 7000, error: 7000 };

function ToastItem({ toast, onDismiss }) {
  const icon = TOAST_ICONS?.[toast?.type] || TOAST_ICONS?.info;
  const style = TOAST_STYLES?.[toast?.type] || TOAST_STYLES?.info;

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg min-w-72 max-w-sm"
      style={{
        backgroundColor: 'var(--color-card)',
        borderColor: style?.borderColor,
        boxShadow: 'var(--shadow-lg)',
        animation: 'slideInRight 0.25s ease-out',
      }}
      role="alert"
    >
      <div className="flex-shrink-0 mt-0.5">
        <Icon name={icon?.name} size={16} color={icon?.color} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>
          {toast?.title}
        </p>
        {toast?.description && (
          <p className="text-xs mt-0.5" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}>
            {toast?.description}
          </p>
        )}
      </div>
      <button
        onClick={() => onDismiss(toast?.id)}
        className="flex-shrink-0 hover:opacity-70 transition-opacity mt-0.5"
        aria-label="Dismiss"
      >
        <Icon name="X" size={14} color="var(--color-muted-foreground)" />
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const counterRef = useRef(0);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev?.filter((t) => t?.id !== id));
  }, []);

  const showToast = useCallback(({ type = 'info', title, description }) => {
    const id = ++counterRef.current;
    const toast = { id, type, title, description };

    setToasts((prev) => {
      const next = [...prev, toast];
      return next?.slice(-4); // max 4 visible
    });

    const delay = AUTO_DISMISS?.[type] || 5000;
    setTimeout(() => dismissToast(id), delay);

    return id;
  }, [dismissToast]);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      {/* Toast container */}
      <div
        className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none"
        aria-live="polite"
        aria-label="Notifications"
      >
        {toasts?.map((toast) => (
          <div key={toast?.id} className="pointer-events-auto">
            <ToastItem toast={toast} onDismiss={dismissToast} />
          </div>
        ))}
      </div>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

export default ToastContext;
